import { useQuery, useMutation } from "@tanstack/react-query";
import { chatApi } from "../api";
import { useChatbotStore, useChatStateStore, useFormStore, useMessagesStore, useProductsStore, useUserStore, useUtmStore } from "../../zustand/store";
import { groupProductsUsingReduce, logToConsole } from "../../utils/utils";
import { useRef, useEffect } from "react";
import { contents } from "../../data/contents";
import { getFormConfig } from "../../data/formConfig";
import { findFastfiveScenarioKeyFromHistory, withFastfiveScenariosFixture } from "../../data/fastfiveScenariosFixture";
import { clearFastfiveContext, loadFastfiveContext, resolveFastfiveRestore } from "../../utils/fastfiveContext";
import { clearLeadFormAttempt } from '../../utils/leadFormAttempt';
import { LayoutModeEnum, EnableModeEnum } from "../../type/modeState.type";

export const useChatbot = (partnerId) => {
    const { chatUserId } = useUserStore();
    const { setChatbot } = useChatbotStore();
    return useQuery({
        queryKey: ['chatbot', partnerId],
        queryFn: async () => {
            const response = await chatApi.get(`${process.env.REACT_APP_CHATBOT_ENDPOINT}/${partnerId}?chatUserId=${chatUserId}`);
            // BE가 scenarios 필드를 내려주기 전까지 fastfive에 한해 fixture를 주입.
            // BE 연동 완료 후 이 한 줄과 fixture 파일을 함께 제거하면 된다.
            const data = withFastfiveScenariosFixture(response.data, partnerId);
            setChatbot(data);
            return data;
        },
        enabled: !!partnerId && !!chatUserId,
    });
};

export const useMessages = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { products, setProducts, clearProducts, setStructuredProducts } = useProductsStore();
    const { setMessages } = useMessagesStore();
    const { currentItemId, setIsChatbotPending, setIsNewProduct, setNewProductCount, language, variant } = useChatStateStore();
    const { setIsFormSubmitted } = useChatStateStore();
    const { refetch: refetchExample } = useExampleQuery();
    const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));

    return useMutation({
        mutationFn: async (variables) => {
            try {
                setIsFormSubmitted('initial');
                const { message, itemId, preAnswers } = variables;
                const response = await chatApi.post(`${process.env.REACT_APP_CHAT_ENDPOINT}/${partnerId}`, {
                    requestMessage: String(message),
                    itemIds: itemId,
                    chatUserId: String(chatUserId),
                    channelId: isMobileDevice ? 'mobile' : 'web',
                    preAnswers: preAnswers,
                    cafe24CVID: gentooStatics?.cafe24CVID,
                    cafe24CVIDY: gentooStatics?.cafe24CVIDY,
                    godomallCVID: gentooStatics?.godomallCVID,
                    variant: String(variant),
                });

                if (response.data.text) {
                    logToConsole(response.data);
                    if (response.data.product.length > 0) {
                        const newProducts = [...response.data.product, ...products];
                        const newStructuredProducts = groupProductsUsingReduce(response.data.product, 3);
                        clearProducts();
                        setProducts(newProducts);
                        setStructuredProducts(newStructuredProducts);
                        setIsNewProduct(true);
                        setNewProductCount(response.data.product.length);
                    }
                    setIsChatbotPending(false);
                    if (currentItemId.length > 0) {
                        refetchExample();
                    }
                    return response.data;
                }
            } catch (error) {
                logToConsole({ textResp: error.message });
                setIsChatbotPending(false);
                console.log('error', error.message);
                setMessages([{ message: contents.message[language].networkError, isChatbot: true }])
            }
        },
    });
};

// custom hook for sending message
export const useSendMessage = () => {
    const { mutate: sendMessages, isPending, isSuccess } = useMessages();
    const { currentItemId } = useChatStateStore();
    const { setMessages } = useMessagesStore();
    const { clearExamples, clearFollowupQuestion } = useProductsStore();

    return {
        sendMessage: (messageInput, isChatbot, preAnswers = []) => {
            clearExamples();
            clearFollowupQuestion();
            sendMessages({
                message: messageInput,
                itemId: currentItemId,
                preAnswers: preAnswers,
            });
            setMessages([{
                message: messageInput,
                isChatbot: isChatbot,
            }]);
        },
        isPending,
        isSuccess,
    };
}

export const useSendMessageWithWebSocket = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { setMessages, streamMessage, clearStreamMessage, setStreamMessage, guidelineContent, guidelineTitle, guidelineId, isBuilder, setIsBuilder } = useMessagesStore();
    const { 
        setIsStreaming, 
        connectionId, 
        setConnectionId, 
        currentItemId, 
        setIsChatbotPending, 
        setIsPDPChat, 
        setIsNewProduct, 
        language, 
        isConsole,
        setIsFormSubmitted,
        setIsLastIntentProductRecommendation,
        variant
    } = useChatStateStore();
    const { clearFollowupQuestion } = useProductsStore();
    const { mutate: refetchProductInfo } = useFetchProductInfo();
    const { refetch: refetchExample } = useExampleQuery();
    const isBuilderRef = useRef(isBuilder);
    const { mutate: postUserEvent } = useUserEvent();
    useEffect(() => {
        isBuilderRef.current = isBuilder;
    }, [isBuilder]);

    return useMutation({
        mutationFn: async (variables) => {
            const { messageInput, preQuestionAnswers = [], preAnswers = [] } = variables;
            const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));

            setIsFormSubmitted('initial');

            setMessages([{
                message: messageInput,
                isChatbot: false,
            }]);

            let messageOutput = '';
            let localConnectionId = null;
            const ws = new WebSocket(process.env.REACT_APP_WSS_CHAT);

            setIsNewProduct(false);
            setIsPDPChat(false);

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    "action": "chat",
                    "chatUserId": chatUserId,
                    "cafe24CVID": gentooStatics?.cafe24CVID,
                    "cafe24CVIDY": gentooStatics?.cafe24CVIDY,
                    "godomallCVID": gentooStatics?.godomallCVID,
                    "channelId": isMobileDevice ? 'mobile' : 'web',
                    "requestMessage": messageInput,
                    "itemIds": currentItemId,
                    "preQuestionAnswers": preQuestionAnswers || [],
                    "preAnswers": preAnswers || [],
                    "externalKey": partnerId,
                    "connectionId": connectionId,
                    "guidelineId": guidelineId,
                    "guidelineTitle": guidelineTitle,
                    "guidelineContent": guidelineContent,
                    "isConsole": isConsole,
                    "variant": String(variant),
                }));
            };

            const productFetchingIntentList = ['제공', '추천', '딥리서치'];
            let intent = null;

            ws.onmessage = (event) => {
                const data = event.data ? JSON.parse(event.data) : null;
                if (data && data.connectionId) {
                    setConnectionId(data.connectionId);
                    localConnectionId = data.connectionId;
                    clearFollowupQuestion();
                } else if (data && data.isBuilder !== undefined) {
                    setIsBuilder(data.isBuilder);
                    isBuilderRef.current = data.isBuilder;
                    window.parent.postMessage({
                        type: 'isBuilderTransition',
                        isBuilder: data.isBuilder,
                    }, '*');
                } else if (data && data.type === 'chatStream') {
                    setIsChatbotPending(false);
                    setStreamMessage(data.token);
                    messageOutput += data.token;
                    intent = data.intent;
                } else if (data && data.guidelineIntent) {
                    window.parent.postMessage({
                        type: 'guidelineIntent',
                        guidelineIntent: data.guidelineIntent,
                    }, '*');
                } else if (data && data.type === 'guidelineContent') {
                    window.parent.postMessage({
                        type: 'guidelineContent',
                        content: data.token,
                    }, '*');
                } else if (data && data.type === 'guidelineTitle') {
                    window.parent.postMessage({
                        type: 'guidelineTitle',
                        title: data.token,
                    }, '*');
                } else if (data && data.done) {
                    window.parent.postMessage({
                        type: 'streamEnd',
                        streamEnd: true,
                        streamType: variables?.streamType || 'chat',
                    }, '*');
                    if (intent && productFetchingIntentList.includes(intent)) {
                        setIsLastIntentProductRecommendation(true);
                        refetchProductInfo(localConnectionId);
                    } else {
                        setIsLastIntentProductRecommendation(false);
                        refetchExample();
                    }
                    const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));
                    postUserEvent({
                        payload: {
                            ...gentooStatics,
                            timestamp: String(Date.now()),
                            eventCategory: 'chat_response_received',
                            context: {
                                responseType: intent,
                            },
                        }
                    });
                    ws.close();
                    setMessages([{
                        message: messageOutput,
                        isChatbot: true,
                        type: 'stream',
                        isBuilder: isBuilderRef.current
                    }]);
                    logToConsole({ text: messageOutput });
                    setIsStreaming(false);
                    clearStreamMessage();
                    setIsChatbotPending(false);
                } else if (data && data.error) {
                    console.log('data.error', data.error);
                    setMessages([{ message: contents.message[language].networkError, isChatbot: true }]);
                    setIsChatbotPending(false);
                    setIsStreaming(false);
                    clearStreamMessage();
                    ws.close();
                }
            };

            ws.onclose = () => {
                console.log('WebSocket connection closed');
            };

            ws.onerror = (error) => {
                setMessages([{ message: contents.message[language].networkError, isChatbot: true }]);
                setIsChatbotPending(false);
                setIsStreaming(false);
                clearStreamMessage();
                ws.close();
                console.warn('WebSocket error:', error);
            };
        },
        enabled: false,
    });
}

export const useSendPDPMessage = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { products, setProducts, clearProducts, setStructuredProducts } = useProductsStore();
    const { setMessages } = useMessagesStore();
    const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));
    const { 
        currentItemId, 
        setIsChatbotPending, 
        setIsNewProduct, 
        setNewProductCount, 
        displayItemId, 
        setIsPDPChat, 
        setCurrentShrinkCardIndex, 
        setCurrentListCardIndex, 
        language,
        setIsLastIntentProductRecommendation,
        setIsFormSubmitted,
        variant
    } = useChatStateStore();
    const { refetch: refetchExample } = useExampleQuery();

    return useMutation({
        mutationFn: async (variables) => {
            try {
                setIsFormSubmitted('initial');
                const { chatType } = variables;
                const response = await chatApi.post(`${process.env.REACT_APP_CHAT_PRODUCT_ENDPOINT}/${partnerId}`, {
                    itemId: String(displayItemId),
                    chatUserId: String(chatUserId),
                    channelId: isMobileDevice ? 'mobile' : 'web',
                    type: chatType,
                    cafe24CVID: gentooStatics?.cafe24CVID,
                    cafe24CVIDY: gentooStatics?.cafe24CVIDY,
                    godomallCVID: gentooStatics?.godomallCVID,
                    variant: String(variant),
                });

                if (response.data.text) {
                    logToConsole(response.data);
                    if (response.data.product.length > 0) {
                        const newProducts = [...response.data.product, ...products];
                        const newStructuredProducts = groupProductsUsingReduce(response.data.product, 3);
                        clearProducts();
                        setProducts(newProducts);
                        setStructuredProducts(newStructuredProducts);
                        setIsNewProduct(true);
                        setNewProductCount(response.data.product.length);
                        setIsPDPChat(true);
                        setIsLastIntentProductRecommendation(true);
                        setCurrentShrinkCardIndex(0);
                        setCurrentListCardIndex(0);
                    } else {
                        setIsNewProduct(false);
                        setIsPDPChat(false);
                    }
                    setIsChatbotPending(false);
                    if (currentItemId.length > 0) {
                        refetchExample();
                    }
                    // "intent": "pdp_start_question",
                    setMessages([{ message: response.data.text[0], isChatbot: true, type: 'pdp' }]);
                    return response.data;
                }
            } catch (error) {
                setMessages([{ message: contents.message[language].networkError, isChatbot: true }])
                logToConsole({ textResp: error.message });
                setIsChatbotPending(false);
                console.log('error', error.message);
            }
        },
        enabled: false,
    });
}

export const useFetchProductInfo = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { currentItemId, setIsNewProduct, setNewProductCount, setIsChatbotPending, setCurrentShrinkCardIndex, setCurrentListCardIndex, currentPage, variant } = useChatStateStore();
    const { products, setProducts, clearProducts, setStructuredProducts } = useProductsStore();
    const { refetch: refetchExample } = useExampleQuery();
    const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));
    return useMutation({
        mutationFn: async (connectionId) => {
            let attempts = 0;
            const maxAttempts = 30;
            const retryDelay = 100;

            while (attempts < maxAttempts) {
                attempts++;
                const response = await chatApi.post(process.env.REACT_APP_CHATSTREAM_PRODUCT_ENDPOINT, {
                    externalKey: partnerId,
                    connectionId: String(connectionId),
                    chatUserId: String(chatUserId),
                    cafe24CVID: gentooStatics?.cafe24CVID,
                    cafe24CVIDY: gentooStatics?.cafe24CVIDY,
                    godomallCVID: gentooStatics?.godomallCVID,
                    variant: String(variant),
                });

                if (response.data.product && response.data.product.length > 0) {
                    const newProducts = [...response.data.product, ...products];
                    const newStructuredProducts = groupProductsUsingReduce(response.data.product, 3);
                    clearProducts();
                    setProducts(newProducts);
                    setStructuredProducts(newStructuredProducts);
                    setCurrentShrinkCardIndex(0);
                    setCurrentListCardIndex(0);
                    setIsNewProduct(true);
                    setNewProductCount(response.data.product.length);
                    break; // Exit the loop if products are found
                }

                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
            if (currentItemId.length > 0) {
                refetchExample();
            }
            setIsChatbotPending(false);

            // Optionally handle the case where no products were found after all attempts
            if (attempts === maxAttempts) {
                console.warn('Max attempts reached without finding products');
            }
        },
        enabled: false,
    });
}

export const useExampleQuery = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { currentItemId, setIsChatbotPending, layoutMode, enableMode, variant } = useChatStateStore();
    const { setExamples, setFollowupQuestion } = useProductsStore();
    const { messages } = useMessagesStore();
    const { chatbot } = useChatbotStore();
    const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));
    return useQuery({
        queryKey: ['example', partnerId, currentItemId],
        queryFn: async () => {
            if (currentItemId.length === 0) {
                return { data: { examples: [], followupQuestion: null } };
            }
            await new Promise(resolve => {
                setTimeout(() => {
                    resolve();
                }, 3000);
            });

            let attempts = 0;
            const maxAttempts = 5;
            const retryDelay = 1000;

            while (attempts < maxAttempts) {
                attempts++;
                const response = await chatApi.get(`${process.env.REACT_APP_EXAMPLE_ENDPOINT}/${partnerId}`,
                    {
                        params: {
                            chatUserId: String(chatUserId),
                            partnerId: String(partnerId),
                            itemIds: currentItemId,
                            cafe24CVID: gentooStatics?.cafe24CVID,
                            cafe24CVIDY: gentooStatics?.cafe24CVIDY,
                            godomallCVID: gentooStatics?.godomallCVID,
                            variant: String(variant),
                        }
                    }
                );

                if (response.data.examples && response.data.examples.length > 0 && messages.length > 0) {
                    if (layoutMode === LayoutModeEnum.MODAL && enableMode === EnableModeEnum.SHRINK &&chatbot.experimentalData?.some(data => data.key === 'csInquiry')) {
                        const formConfig = getFormConfig(partnerId);
                        const examples = Array.isArray(response.data.examples) ? response.data.examples : [];
                        setExamples([formConfig.triggerButtonText, ...examples]);
                    } else {
                        setExamples(response.data.examples);
                    }
                    setFollowupQuestion(response.data.followupQuestion);
                    logToConsole({ examples: response.data.examples, itemIds: currentItemId });
                    setIsChatbotPending(false);
                    return response;
                }

                if (response.data.examples.length === 0 && attempts < maxAttempts) {
                    logToConsole({ examples: response.data.examples, itemIds: currentItemId });
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
            }

            // Final attempt only if all retries failed
            const finalResponse = await chatApi.get(`${process.env.REACT_APP_EXAMPLE_ENDPOINT}/${partnerId}`,
                {
                    params: {
                        chatUserId: String(chatUserId),
                        partnerId: String(partnerId),
                        itemIds: currentItemId,
                        cafe24CVID: gentooStatics?.cafe24CVID,
                        cafe24CVIDY: gentooStatics?.cafe24CVIDY,
                        godomallCVID: gentooStatics?.godomallCVID,
                        variant: String(variant),
                    }
                }
            );
            return finalResponse;
        },
        enabled: false,
    });
};

export const useResetQuery = () => {
    const { chatUserId, partnerId } = useUserStore();
    const { clearMessages } = useMessagesStore();
    const { clearProducts, clearStructuredProducts, clearExamples, clearFollowupQuestion } = useProductsStore();
    const {
        setIsPreAnswered,
        setIsFormSubmitted,
        setCurrentItemId,
        clearCurrentShrinkCardIndex,
        clearCurrentListCardIndex,
        clearFastfiveScenarioId,
    } = useChatStateStore();
    const { clearPreAnswersObject } = useFormStore();
    return useMutation({
        mutationFn: async () => {
            const response = await chatApi.post(`${process.env.REACT_APP_RENEW_ENDPOINT}/${partnerId}`, {
                chatUserId: String(chatUserId),
            });

            clearMessages();
            clearProducts();
            clearStructuredProducts();
            clearExamples();
            clearFollowupQuestion();
            clearCurrentShrinkCardIndex();
            clearCurrentListCardIndex();
            setCurrentItemId([]);
            setIsPreAnswered(false);
            setIsFormSubmitted('initial');
            // fastfive 시나리오 모드: scenarioId 도 초기화해서 entry 선택 화면으로 돌아가도록.
            // (시나리오 모드 아닌 파트너는 fastfiveScenarioId 가 null 이라 no-op)
            clearFastfiveScenarioId();
            // 시나리오를 버리면 그 시나리오의 사전질문 답변(인원/평수)도 함께 버린다.
            clearPreAnswersObject();
            clearFastfiveContext(partnerId);
            clearLeadFormAttempt();
            return response;
        }
    });
}

export const useLoadHistory = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { setMessages, clearMessages } = useMessagesStore();
    const { setProducts, clearProducts, setStructuredProducts, clearStructuredProducts, clearExamples } = useProductsStore();
    const { setIsPreAnswered, setFastfiveScenarioId } = useChatStateStore();
    const { setPreAnswersObject } = useFormStore();

    return useQuery({
        queryKey: ['loadHistory', partnerId],
        queryFn: async () => {
            const response = await chatApi.get(`${process.env.REACT_APP_CHAT_ENDPOINT}/load/${partnerId}`, {
                params: {
                    partnerId: String(partnerId),
                    chatUserId: String(chatUserId),
                }
            });

            const formattedHistory = response.data.chatList
                .filter(message => message.text.length > 0)
                .map(message => ({
                    message: message.text,
                    isChatbot: message.role === 'assistant',
                }));
            let productList = response.data.chatList
                .filter(message => message.role === 'assistant')
                .map(message => [...message.product])
                .flat()
                .reverse();

            const structuredProductList = response.data.chatList
                .filter(message => message.role === 'assistant')
                .filter(message => message.product && message.product.length > 0)
                .reverse()
                .map(message => {
                    const reversedProducts = [...message.product].reverse();
                    const groupedProducts = groupProductsUsingReduce(reversedProducts, 3);
                    return groupedProducts;
                })
                .flat()
            clearMessages();
            clearProducts();
            clearStructuredProducts();
            if (productList.length === 0) {
                clearExamples();
            }
            setMessages(formattedHistory);
            setProducts(productList);
            setStructuredProducts(structuredProductList);

            // history 에 대화 내용이 있으면 isPreAnswered=true 로 복원.
            // 새로고침 시 Transaction 이 isPreAnswered 를 false 로 초기화하기 때문에,
            // 복원하지 않으면 ChatInput · Carousel 가드(isPreAnswered) 가 false 로 막혀
            // 입력창과 캐러셀이 활성화되지 않음.
            if (formattedHistory.length > 0) {
                setIsPreAnswered(true);
            }

            // 서버가 대화를 돌려줬을 때만 저장된 시나리오·사전답변을 되살린다.
            // 대화가 비었으면 서버 기준으로 만료된 것이므로 컨텍스트도 쓰지 않고
            // entry 화면이 뜨게 둔다. 보관 기간 판단은 계속 서버가 한다.
            if (formattedHistory.length > 0) {
                const restore = resolveFastfiveRestore(
                    loadFastfiveContext(partnerId),
                    useChatbotStore.getState().chatbot?.scenarios,
                    useChatStateStore.getState().fastfiveScenarioId,
                );
                // entry 버튼 클릭과 동일 취급 → scenario greeting 은 노출하지 않는다.
                if (restore.scenarioKey) setFastfiveScenarioId(restore.scenarioKey, false);
                // 사전답변이 없으면 리드의 userCount 가 1 로, officeArea 가 빈 값으로 나간다.
                if (restore.preAnswersObject
                    && Object.keys(useFormStore.getState().preAnswersObject).length === 0) {
                    setPreAnswersObject(restore.preAnswersObject);
                }
            }

            // 컨텍스트가 없는 경우(수정 전에 시작된 대화, localStorage 차단 등)
            // 복원된 대화의 요청 메시지로 시나리오만이라도 되찾는다.
            if (!useChatStateStore.getState().fastfiveScenarioId) {
                const recoveredScenarioKey = findFastfiveScenarioKeyFromHistory(
                    formattedHistory,
                    useChatbotStore.getState().chatbot?.scenarios,
                );
                // entry 버튼 클릭과 동일 취급 → scenario greeting 은 노출하지 않는다.
                if (recoveredScenarioKey) setFastfiveScenarioId(recoveredScenarioKey, false);
            }

            return response;
        },
        enabled: false,
    });
}

export const useLeadFormOpened = () => {
    const { chatUserId, partnerId } = useUserStore();
    return useMutation({
        mutationFn: ({ formAttemptId, scenarioKey }) => chatApi.post(
            `${process.env.REACT_APP_FORM_ENDPOINT.replace(/\/form\/?$/, '/form-opened')}/${partnerId}`,
            { chatUserId: String(chatUserId), formAttemptId, scenarioKey: scenarioKey || undefined },
        ),
        retry: 1,
        onError: () => console.warn('Lead form reach could not be recorded'),
    });
};

export const useFormSubmitted = () => {
    const { chatUserId, partnerId } = useUserStore();
    const { currentItemId } = useChatStateStore();
    const { utmParams } = useUtmStore();

    return useMutation({
        mutationFn: async (variables) => {
            const { formData = {}, formAttemptId } = variables;
            const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));
            const response = await chatApi.post(`${process.env.REACT_APP_CSINQUIRY_ENDPOINT}/${partnerId}`,
                {
                    chatUserId: String(chatUserId),
                    channelId: 'web',
                    formAttemptId,
                    itemId: [String(currentItemId)],
                    formData: {
                        name: formData.name,
                        phone: formData.phone,
                        inquiry: formData.inquiry,
                    },
                },
                {
                    params: {
                        utm_source: utmParams?.source,
                        utm_medium: utmParams?.medium,
                        utm_campaign: utmParams?.campaign,
                        utm_content: utmParams?.content,
                        utm_term: utmParams?.term,
                        transitions_page: utmParams?.transitionPage,
                        fbclid: gentooStatics?.fbclid,
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },

            );
            return response.data;
        },
    });
}

export const useFormSubmittedFastfive = () => {
    const { chatUserId, partnerId } = useUserStore();
    const { currentItemId } = useChatStateStore();
    const { utmParams } = useUtmStore();

    return useMutation({
        mutationFn: async (variables) => {
            const { formData = {}, scenarioKey, officeArea, userCount, formAttemptId } = variables;
            const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));
            const leadTracking = gentooStatics?.leadTracking || {};
            const deviceType = leadTracking.deviceType
                || (gentooStatics?.deviceType === 'web' ? 'pc' : gentooStatics?.deviceType);
            const response = await chatApi.post(`${process.env.REACT_APP_FORM_ENDPOINT}/${partnerId}`,
                {
                    chatUserId: String(chatUserId),
                    channelId: 'web',
                    formAttemptId,
                    itemId: [String(currentItemId)],
                    // 시나리오별 form 응답 분기를 위해 scenarioKey 전달.
                    // 시나리오 모드가 아닌 호출에서는 undefined 가 들어가 payload 에서 자동 누락됨.
                    scenarioKey: scenarioKey || undefined,
                    formData: {
                        name: formData.name,
                        phone: formData.phone,
                        email: formData.email,
                        checkPrivacyPolicy: formData.checkPrivacyPolicy,
                        checkMarketing: formData.checkMarketing,
                        userCount,
                        officeArea,
                        gclid: leadTracking.gclid,
                        conversionPage: leadTracking.conversionPage || utmParams?.transitionPage,
                        prevConversionPage: leadTracking.prevConversionPage,
                        landing: leadTracking.landing,
                        referrer: leadTracking.referrer,
                        deviceType,
                    },
                },
                {
                    params: {
                        utm_source: utmParams?.source,
                        utm_medium: utmParams?.medium,
                        utm_campaign: utmParams?.campaign,
                        utm_content: utmParams?.content,
                        utm_term: utmParams?.term,
                        transitions_page: utmParams?.transitionPage,
                        fbclid: gentooStatics?.fbclid,
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },

            );
            return response.data;
        },
    });
}

export const useUserEvent = () => {
    const { partnerId } = useUserStore();

    return useMutation({
        mutationFn: async (variables) => {
            const gentooStatics = JSON.parse(sessionStorage.getItem('gentoo-statics'));
            if (!gentooStatics) {
                console.warn('gentoo-statics not found');
                return;
            }
            const params = {
                "experiment_id": String(gentooStatics.experimentId),
                "partner_id": String(gentooStatics.partnerId),
                "variant": String(gentooStatics.variantId) || 'control',
                "session_id": String(gentooStatics.sessionId),
                "user_id": String(gentooStatics.chatUserId),
                "user_type": String(gentooStatics.userType) || 'guest',
                "display_location": String(gentooStatics.displayLocation) || undefined,
                "device_type": String(gentooStatics.deviceType) || undefined,
                "timestamp": String(Date.now()),
                "event_name": String(variables.payload.eventCategory),
                "context": variables.payload?.context || undefined,
            };
            const response = await chatApi.post(`${process.env.REACT_APP_USEREVENT_ENDPOINT}/${partnerId}`, params);
            return response;
        },
    });
}

export const useUserEventLegacy = () => {
    const { chatUserId, partnerId, isMobileDevice } = useUserStore();

    return useMutation({
        mutationFn: async (variables) => {
            const response = await chatApi.post(`${process.env.REACT_APP_USEREVENT_LEGACY_ENDPOINT}/${partnerId}`, {
                channelId: String(isMobileDevice ? 'mobile' : 'web'),
                eventCategory: String(variables.eventCategory),
                chatUserId: String(chatUserId),
                products: [{ ...variables.product, quantity: 1 }],
            });
            return response;
        },
    });
}

export const useHistoryUpdate = () => {
    const { partnerId, chatUserId } = useUserStore();
    const { displayLocation } = useChatStateStore();

    return useMutation({
        mutationFn: async (variables) => {
            const payload = {
                chatUserId: String(chatUserId),
                pageUrl: variables.url,
                displayLocation: displayLocation,
                itemId: variables.itemId,
            }
            const response = await chatApi.post(`${process.env.REACT_APP_HISTORY_ENDPOINT}/${partnerId}`, payload);
            return response.data.visitedState;
        },
    });
}

export const useWSSOpen = () => {
    const { partnerId, chatUserId, isMobileDevice } = useUserStore();
    const { guidelineContent, guidelineTitle, guidelineId } = useMessagesStore();
    const { connectionId, currentItemId, setIsPDPChat, setIsNewProduct, isConsole } = useChatStateStore();

    return useMutation({
        mutationFn: async (variables) => {
            const { messageInput, preQuestionAnswers = [], preAnswers = [] } = variables;

            let messageOutput = '';
            let localConnectionId = null;
            const ws = new WebSocket(process.env.REACT_APP_WSS_CHAT);

            setIsNewProduct(false);
            setIsPDPChat(false);

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    "action": "chat",
                    "chatUserId": chatUserId,
                    "channelId": isMobileDevice ? 'mobile' : 'web',
                    "requestMessage": 'connection test',
                    "itemIds": currentItemId,
                    "preQuestionAnswers": preQuestionAnswers || [],
                    "preAnswers": preAnswers || [],
                    "externalKey": partnerId,
                    "connectionId": connectionId,
                    "guidelineId": guidelineId,
                    "guidelineTitle": guidelineTitle,
                    "guidelineContent": guidelineContent,
                    "isConsole": isConsole,
                }));
            };
        },
        enabled: false,
    });
}


export const useFetchProductOptions = (itemId) => {
    const { partnerId } = useUserStore();
    return useQuery({
        queryKey: ['productOptions', itemId],
        queryFn: async () => {
            const response = await chatApi.get(`${process.env.REACT_APP_SHOP_OPTIONS_ENDPOINT}`,
                {
                    params: {
                        partnerId: String(partnerId),
                        itemId: String(itemId),
                    }
                }
            );
            return response.data;
        },
        enabled: !!partnerId && !!itemId,
    });
}

export const useFetchProductAllVariants = (itemId) => {
    const { partnerId } = useUserStore();
    return useQuery({
        queryKey: ['productAllVariants', itemId],
        queryFn: async () => {
            const response = await chatApi.get(`${process.env.REACT_APP_SHOP_VARIANT_ENDPOINT}`, {
                params: {
                    partnerId: String(partnerId),
                    itemId: String(itemId),
                }
            })
            return response.data;
        },
        enabled: !!partnerId && !!itemId,
    })
}

export const useFetchProductOptionVariant = () => {
    const { partnerId } = useUserStore();
    return useMutation({
        mutationFn: async ({ itemId, options }) => {
            const response = await chatApi.post(`${process.env.REACT_APP_SHOP_VARIANT_ENDPOINT}`, {
                partner_id: String(partnerId),
                item_id: String(itemId),
                options: options
            })
            return response.data;
        },
    })
}
